import { Card, CardContent } from "@/components/ui/card";
import { groupEntriesByIsoWeek } from "@/lib/employee-card";
import { formatHours } from "@/lib/format";

interface TimeEntry {
  id: string;
  date: string;
  project_id: string;
  project_name: string;
  task_description: string | null;
  duration_seconds: number;
}

interface TimeEntriesTabProps {
  timeEntries: TimeEntry[];
}

const dayLabel = (date: string) =>
  new Date(date).toLocaleDateString("nb-NO", { weekday: "short", day: "2-digit", month: "2-digit" });

const rangeLabel = (start: Date, end: Date) =>
  `${start.toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}–${end.toLocaleDateString("nb-NO", { day: "numeric", month: "short" })}`;

export function TimeEntriesTab({ timeEntries }: TimeEntriesTabProps) {
  const weeks = groupEntriesByIsoWeek(timeEntries);
  const total = weeks.reduce((sum, w) => sum + w.totalHours, 0);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-baseline gap-x-2.5 px-5 py-3">
          <h2 className="font-display text-base font-semibold">Timelogg</h2>
          <span className="text-sm text-muted-foreground">siste 30 dager · {formatHours(total)}</span>
        </div>

        {weeks.length === 0 ? (
          <p className="border-t border-border px-5 py-8 text-center text-sm text-muted-foreground">
            Ingen timeføringer de siste 30 dagene.
          </p>
        ) : (
          weeks.map((week) => (
            <section key={week.weekStart} aria-label={`Uke ${week.weekNumber}`}>
              <div className="flex items-baseline justify-between border-t border-border bg-muted/50 px-5 py-2.5 text-xs font-medium">
                <span className="uppercase tracking-wider">
                  Uke {week.weekNumber}
                  <span className="ml-1.5 font-normal normal-case tracking-normal text-muted-foreground">
                    · {rangeLabel(week.start, week.end)}
                  </span>
                </span>
                <span className="tabular-nums">{formatHours(week.totalHours)}</span>
              </div>
              <ul>
                {week.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center gap-3 border-t border-border/50 px-5 py-2.5 text-sm"
                  >
                    <span className="w-24 shrink-0 text-muted-foreground">{dayLabel(entry.date)}</span>
                    <span className="shrink-0 font-medium">{entry.project_name}</span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{entry.task_description}</span>
                    <span className="w-20 shrink-0 text-right font-medium tabular-nums">
                      {formatHours(entry.duration_seconds / 3600)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </CardContent>
    </Card>
  );
}
